/* Automations page, after the click demo's agent cards, runs table and editor dialog. Self-contained:
   composing from another module makes Vite warn about PostCSS; buttons and dialog match the integrations page. */
.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: 28px 40px 48px;
  overflow-y: auto;
  min-width: 0;
}

.empty {
  margin: 0;
  color: var(--muted);
}

.muted {
  margin: 0;
  color: var(--muted);
  font-size: 14px;
}

.notice {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  background: var(--warning-soft);
  border-radius: var(--radius);
  padding: var(--space-2) var(--space-2) var(--space-2) 16px;
}

.notice span {
  flex: 1;
}

.close {
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  color: var(--text);
  font-size: 22px;
  cursor: pointer;
}

.attention {
  margin: 0;
  align-self: flex-start;
  font-weight: 700;
  background: var(--warning-soft);
  border-radius: var(--radius);
  padding: var(--space-2) 14px;
}

.grid {
  display: grid;
  /* As many 340px columns as fit, but never more than three. */
  grid-template-columns: repeat(auto-fill, minmax(max(340px, calc((100% - 2 * var(--space-4)) / 3)), 1fr));
  gap: var(--space-4);
}

.card {
  background: var(--surface);
  border-radius: 12px;
  box-shadow: 0 3px 8px rgb(2 48 71 / 10%);
  padding: var(--space-4) 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cardHead {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
}

.cardTitle {
  flex: 1;
  min-width: 0;
}

/* The click demo's switch: 48×28 track in a 56×44 touch target. */
.switch {
  flex: none;
  width: 56px;
  height: 44px;
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.track {
  position: relative;
  display: block;
  width: 48px;
  height: 28px;
  border-radius: 999px;
  background: #b9cbd6;
  transition: background 0.15s;
}

.knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgb(2 48 71 / 30%);
  transition: left 0.15s;
}

.switch[aria-pressed='true'] .track {
  background: var(--accent);
}

.switch[aria-pressed='true'] .knob {
  left: 23px;
}

.switch:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 2px;
  border-radius: var(--radius);
}

.pill {
  display: inline-flex;
  margin-top: 6px;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 700;
}

.off {
  composes: pill;
  background: var(--surface-2);
  color: var(--text);
}

.name {
  margin: 0;
  font-size: 20px;
  font-weight: 900;
}

.on {
  composes: pill;
  background: var(--accent);
  color: var(--accent-text);
}

.warn {
  composes: pill;
  background: var(--warning-soft);
  color: var(--text);
}

.bad {
  composes: pill;
  background: var(--danger-soft);
  color: var(--danger);
}

.prompt {
  margin: 0;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.facts {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  gap: 6px 12px;
  margin: 0;
  font-size: 14px;
  align-items: baseline;
}

.facts dt {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

.facts dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: auto;
}

.button {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 18px;
  border: none;
  border-radius: var(--radius);
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
}

.primary {
  composes: button;
  background: var(--accent);
  color: var(--accent-text);
}

.primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.secondary {
  composes: button;
  background: var(--surface-2);
  color: var(--text);
}

.confirm {
  composes: button;
  background: var(--eyebrow);
  color: #ffffff;
}

.back {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  align-self: flex-start;
  min-height: 44px;
  border: none;
  background: transparent;
  color: var(--link);
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}

.tableWrap {
  background: var(--surface);
  border-radius: 12px;
  box-shadow: 0 3px 8px rgb(2 48 71 / 10%);
  overflow-x: auto;
}

.table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
}

.table th,
.table td {
  text-align: left;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border);
  vertical-align: top;
}

.table th {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

.link {
  text-decoration: none;
  border: none;
  background: transparent;
  padding: 0;
  min-height: 32px;
  font: inherit;
  font-weight: 700;
  color: var(--link);
  cursor: pointer;
  text-align: left;
}

.backdrop {
  position: fixed;
  inset: 0;
  background: rgb(2 48 71 / 55%);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 140px var(--space-4) var(--space-4);
  z-index: 20;
  padding-top: 48px;
  overflow-y: auto;
}

.dialog {
  background: var(--surface);
  border-radius: 14px;
  box-shadow: 0 12px 32px rgb(2 48 71 / 35%);
  padding: 24px 26px;
  width: 100%;
  max-width: 680px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.confirmDialog {
  composes: dialog;
  max-width: 460px;
  gap: 14px;
}

.dialogTitle {
  margin: 0;
  font-size: 22px;
}

.dialogActions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.text {
  margin: 0;
  line-height: 1.5;
}

.field,
.fieldset legend,
.inline {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field input,
.field textarea,
.inline input,
.inline select {
  border: none;
  border-radius: var(--radius);
  background: var(--surface-2);
  font: inherit;
  font-size: 15px;
  letter-spacing: 0;
  text-transform: none;
  color: var(--text);
}

.field input {
  min-height: 44px;
  padding: 0 14px;
}

.field textarea {
  padding: 12px 14px;
  resize: vertical;
  line-height: 1.5;
}

.fieldset {
  border: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.fieldset legend {
  padding: 0;
  margin-bottom: var(--space-2);
}

.chips {
  display: flex;
  gap: var(--space-2);
  flex-wrap: wrap;
}

.chip {
  min-height: 40px;
  padding: 0 14px;
  border: none;
  border-radius: 999px;
  background: var(--surface-2);
  color: var(--text);
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}

.chip[aria-pressed='true'] {
  background: var(--chrome);
  color: var(--chrome-text);
  font-weight: 700;
}

.row {
  display: flex;
  gap: var(--space-4);
  flex-wrap: wrap;
  align-items: center;
}

.inline {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

.inline input,
.inline select {
  min-height: 40px;
  padding: 0 10px;
}

.hint {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
}

.tools {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 4px var(--space-3);
  max-height: 220px;
  overflow-y: auto;
}

.tool {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-height: 36px;
  font-size: 14px;
}

.tool input {
  width: 18px;
  height: 18px;
  flex: none;
}

/* Segmented tabs as in the click demo. */
.tabs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-self: flex-start;
  background: var(--surface-2);
  border-radius: 12px;
  padding: 4px;
}

.tab {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  min-height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: var(--text);
  font: inherit;
  font-size: 15px;
  cursor: pointer;
}

.tab[aria-current='page'] {
  background: var(--surface);
  font-weight: 700;
  box-shadow: 0 2px 6px rgb(2 48 71 / 10%);
}

.plan {
  background: var(--surface);
  border-radius: 12px;
  box-shadow: 0 3px 8px rgb(2 48 71 / 10%);
  padding: var(--space-4) 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.legend {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  align-items: center;
  font-size: 14px;
}

.label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted);
}

.legendItem {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.swatch {
  width: 14px;
  height: 14px;
  border-radius: 4px;
}

.swatchOn {
  composes: swatch;
  background: var(--accent);
}

.swatchOff {
  composes: swatch;
  background: var(--surface-2);
}

.tableScroll {
  overflow-x: auto;
}

/* Scrolls only when the window is too narrow for 220px plus seven day columns. */
.planTable {
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
  table-layout: fixed;
}

/* Padding instead of border-spacing: the gaps between cells stay inside the table, so nothing overflows. */
.planTable th,
.planTable td {
  padding: 4px;
}

.planTable th {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
  text-align: center;
}

.planTable thead th:first-child {
  width: 220px;
  text-align: left;
}

.planTable thead th[aria-current='date'] {
  color: var(--text);
}

.planTable tbody th {
  text-align: left;
  letter-spacing: 0;
  text-transform: none;
  color: var(--text);
  font-size: 15px;
}

.planTable tbody th > * {
  display: block;
  overflow-wrap: anywhere;
}

.planTable tbody th .muted {
  font-size: 13px;
  font-weight: 400;
}

.cell {
  min-height: 44px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
}

.cellOn {
  composes: cell;
  background: var(--accent);
  color: var(--accent-text);
}

.cellOff {
  composes: cell;
  background: var(--surface-2);
  color: var(--muted);
}

.cellEmpty {
  composes: cell;
  background: var(--bg);
}

/* Automation page: the whole prompt and all facts, then its runs. */
.nameLink {
  color: inherit;
  text-decoration: none;
}

.nameLink:hover {
  text-decoration: underline;
}

.detail {
  background: var(--surface);
  border-radius: 12px;
  box-shadow: 0 3px 8px rgb(2 48 71 / 10%);
  padding: var(--space-4) 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 960px;
}

.fullPrompt {
  margin: 0;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  background: var(--bg);
  border-radius: var(--radius);
  padding: var(--space-3) 14px;
}

.sectionTitle {
  margin: var(--space-2) 0 0;
  font-size: 19px;
}

/* Run page: a bar with the facts above the timeline, which scrolls like the chat. */
.runBar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2) var(--space-4);
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--border);
  background: var(--surface);
}

.runBar .back {
  min-height: 36px;
}

.runFacts {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin: 0;
  font-size: 14px;
  align-items: baseline;
}

.runFacts dt {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

.runFacts dd {
  margin: 0 var(--space-3) 0 0;
}

.runBar .muted {
  flex-basis: 100%;
}

.runEmpty {
  margin: 0;
  padding: var(--space-4);
  color: var(--muted);
}

@media (max-width: 720px) {
  .main {
    padding: var(--space-4) 16px 32px;
  }

  .grid {
    grid-template-columns: 1fr;
  }

  .facts {
    grid-template-columns: 100px minmax(0, 1fr);
  }
}
